import { useScrollReveal } from "@/hooks/useScrollReveal"
import {Headphones, MessageSquare, BookOpen, PenTool} from 'lucide-react'

const skills = [
  {
    icon: Headphones,
    emoji: "🎧",
    title: "Ouvir",
    subtitle: "Entenda inglês na vida real",
    desc: "Treine sua escuta com áudios reais, sotaques variados e situações do dia a dia profissional. Você aprende a entender inglês fora do \"modo didático\".",
    highlight: "Não é áudio de livro. É inglês como ele é falado.",
    color: "#0E8EA0",
  },
  {
    icon: MessageSquare,
    emoji: "🗣",
    title: "Falar",
    subtitle: "Ganhe confiança para se comunicar",
    desc: "Pratique conversação no WhatsApp, simulações reais e aulas ao vivo. Receba correção imediata e destrave a fala.",
    highlight: "Você não decora frases. Você aprende a se expressar.",
    color: "#B038B2",
  },
  {
    icon: BookOpen,
    emoji: "📖",
    title: "Ler",
    subtitle: "Leia com segurança e interpretação real",
    desc: "Desenvolva leitura aplicada a e-mails, relatórios, entrevistas e textos profissionais. Aprenda a compreender contexto, não apenas traduzir palavras.",
    highlight: "Inglês útil para o seu trabalho.",
    color: "#6CC4C4",
  },
  {
    icon: PenTool,
    emoji: "✍",
    title: "Escrever",
    subtitle: "Escreva com clareza e feedback personalizado",
    desc: "Produza textos, mensagens e comunicações profissionais com orientação e correção. Evolua na prática com retorno estruturado.",
    highlight: "Você escreve melhor porque recebe direção.",
    color: "#F5C542",
  },
]

export default function MethodologySection() {
  const { ref, isVisible } = useScrollReveal()

  return (
    <section id="metodologia" className="py-20 md:py-28 bg-white">
      <div ref={ref} className="max-w-6xl mx-auto px-5">
        {/* Header */}
        <div
          className={`text-center max-w-3xl mx-auto mb-16 transition-all duration-700 ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-gray-900 mb-6 leading-tight">
            Nossa metodologia transforma
            <br />
            <span style={{ color: "#0E8EA0" }}>estudo em ação.</span>
          </h2>
          <p className="text-gray-600 text-lg md:text-xl leading-relaxed">
            Na English Work, cada aula é construída para você aplicar imediatamente no trabalho, em viagens e na sua vida profissional — com prática real, conversação diária e acompanhamento contínuo.
          </p>
        </div>

        {/* Skills grid - 2 per row */}
        <div className="grid sm:grid-cols-2 gap-6">
          {skills.map((skill, i) => (
            <div
              key={skill.title}
              className={`group relative bg-gray-50 rounded-2xl p-7 border-l-4 hover:shadow-xl hover:-translate-y-1 transition-all duration-500 ${
                isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
              }`}
              style={{
                borderLeftColor: skill.color,
                transitionDelay: `${200 + i * 120}ms`,
              }}
            >
              <div className="flex items-start gap-5">
                <div
                  className="w-14 h-14 rounded-2xl flex items-center justify-center shrink-0 group-hover:scale-110 transition-transform duration-300"
                  style={{ backgroundColor: `${skill.color}15` }}
                >
                  <skill.icon size={26} style={{ color: skill.color }} />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xl">{skill.emoji}</span>
                    <h3 className="text-xl font-extrabold text-gray-900">{skill.title}</h3>
                  </div>
                  <p className="text-sm font-semibold mb-2" style={{ color: skill.color }}>
                    {skill.subtitle}
                  </p>
                  <p className="text-gray-500 text-sm leading-relaxed mb-3">{skill.desc}</p>
                  <p className="text-sm font-bold" style={{ color: skill.color }}>
                    👉 {skill.highlight}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}
